import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Server,
  Share2,
  Map as MapIcon,
  Cable,
  Bell,
  Activity,
  Settings as SettingsIcon,
  LogOut,
  Radio,
  Menu,
  MoreHorizontal,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLive } from "@/context/LiveContext";
import { cn } from "@/lib/utils";
import ThemeToggle from "@/components/ThemeToggle";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/pops", label: "POP", icon: Server },
  { to: "/topology", label: "Topology", icon: Share2 },
  { to: "/map", label: "Map", icon: MapIcon },
  { to: "/links", label: "Links", icon: Cable },
  { to: "/alerts", label: "Alerts", icon: Bell },
  { to: "/monitoring", label: "Monitoring", icon: Activity },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

const BOTTOM = [
  { to: "/", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/pops", label: "POP", icon: Server },
  { to: "/map", label: "Map", icon: MapIcon },
  { to: "/alerts", label: "Alerts", icon: Bell },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { connected } = useLive();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-dvh flex bg-background text-foreground">
      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <aside className={cn(
        "w-60 shrink-0 border-r border-border bg-card flex flex-col h-dvh z-40",
        "fixed lg:sticky top-0 lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        <div className="h-14 flex items-center gap-2 px-4 border-b border-border pt-[env(safe-area-inset-top)]">
          <div className="h-7 w-7 rounded-md bg-primary flex items-center justify-center">
            <Radio className="h-4 w-4 text-primary-foreground" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-black text-sm tracking-tight">POP MONITOR</div>
            <div className="text-[10px] text-muted-foreground font-mono">NOC / FTTH</div>
          </div>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150",
                  isActive
                    ? "bg-primary/15 text-primary border border-primary/30"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent border border-transparent"
                )
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-md bg-secondary flex items-center justify-center font-heading font-bold text-xs uppercase">
              {user?.name?.[0] || "U"}
            </div>
            <div className="min-w-0">
              <div className="text-xs font-medium truncate">{user?.name}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{user?.role}</div>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs text-muted-foreground hover:text-red-400 hover:bg-red-500/10 border border-border transition-colors duration-150"
          >
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 shrink-0 border-b border-border bg-background/95 backdrop-blur flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20 pt-[env(safe-area-inset-top)]">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-muted-foreground hover:text-foreground" onClick={() => setOpen(true)} data-testid="sidebar-toggle"><Menu className="h-5 w-5" /></button>
            <div className="text-xs font-mono text-muted-foreground hidden sm:block">
              POP Network Monitoring &amp; Topology
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs font-mono" data-testid="live-indicator">
            <ThemeToggle />
            <span className={cn("relative flex h-2 w-2")}>
              {connected && (
                <span className="live-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500" />
              )}
              <span
                className={cn(
                  "relative inline-flex rounded-full h-2 w-2",
                  connected ? "bg-emerald-500" : "bg-muted-foreground/50"
                )}
              />
            </span>
            <span className={connected ? "text-emerald-500" : "text-muted-foreground"}>
              {connected ? "LIVE" : "OFFLINE"}
            </span>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto pb-[calc(4rem+env(safe-area-inset-bottom))] lg:pb-0">{children}</main>
      </div>

      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t border-border bg-card/95 backdrop-blur pb-[env(safe-area-inset-bottom)]"
        data-testid="bottom-nav"
      >
        <div className="grid grid-cols-5 h-14">
          {BOTTOM.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`bottom-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium",
                  isActive ? "text-primary" : "text-muted-foreground"
                )
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
          <button
            type="button"
            data-testid="bottom-nav-more"
            onClick={() => setOpen(true)}
            className="flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-muted-foreground"
          >
            <MoreHorizontal className="h-4 w-4" />
            More
          </button>
        </div>
      </nav>
    </div>
  );
}
